import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Navigate } from 'react-router-dom';
import { logError } from '../../utils/logger';
import apiService from '../../services/api';
import './Stats.scss';

const Stats = () => {
  const { user, isAuthenticated, loading } = useAuth();
  const [gameStats, setGameStats] = useState([]);
  const [currentStreaks, setCurrentStreaks] = useState({});
  const [summary, setSummary] = useState(null);
  const [sortField, setSortField] = useState('username');
  const [sortDirection, setSortDirection] = useState('asc');
  const [isLoading, setIsLoading] = useState(true);

  // Get today's date in YYYY-MM-DD format
  const todayDate = new Date().toISOString().split('T')[0];

  // All available games
  const allGames = [
    { name: 'slay-the-spire', displayName: 'Slay the Spire' },
    { name: 'bible-books', displayName: 'Bible Books' },
    { name: 'subnautica-creatures', displayName: 'Subnautica Creatures' },
    { name: 'aoe2-units', displayName: 'Age of Empires II Units' }
  ];

  useEffect(() => {
    if (isAuthenticated && user) {
      setIsLoading(true);
      // Run all API calls in parallel for better performance
      Promise.all([
        fetchGameStats(),
        fetchCurrentStreaks(),
        fetchSummary()
      ]).finally(() => {
        setIsLoading(false);
      });
    }
  }, [isAuthenticated, user]);

  const fetchGameStats = async () => {
    try {
      const response = await apiService.get(`/api/game-stats/user/${user.id}`);
      const stats = await response.json();
      setGameStats(stats);
    } catch (error) {
      logError('Error fetching game stats', error);
    }
  };

  const fetchCurrentStreaks = async () => {
    try {
      const response = await apiService.get(`/api/daily-game-states/user/${user.id}/summary/${todayDate}`);
      const data = await response.json();
      
      // Extract current streaks from the gameData object
      const streaks = {};
      Object.entries(data.gameData || {}).forEach(([gameId, gameInfo]) => {
        streaks[gameId] = gameInfo.currentStreak || 0;
      });
      setCurrentStreaks(streaks);
    } catch (error) {
      logError('Error fetching current streaks', error);
      // Set default empty streaks on error
      setCurrentStreaks({});
    }
  };

  const fetchSummary = async () => {
    try {
      const response = await apiService.get(`/api/game-stats/user/${user.id}/summary`);
      const summaryData = await response.json();
      setSummary(summaryData);
    } catch (error) {
      logError('Error fetching summary stats', error);
    }
  };


  const handleSort = (field) => {
    if (field === sortField) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortData = (data) => {
    return [...data].sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];

      if (sortField === 'username') {
        aValue = getGameDisplayName(a.game_name);
        bValue = getGameDisplayName(b.game_name);
      } else if (sortField === 'current_daily_streak') {
        aValue = currentStreaks[a.game_name] || 0;
        bValue = currentStreaks[b.game_name] || 0;
      }

      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      let comparison = 0;
      if (sortDirection === 'asc') {
        comparison = aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
      } else {
        comparison = aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
      }

      // If primary sort values are equal, sort by game name alphabetically as tiebreaker
      if (comparison === 0) {
        const aName = getGameDisplayName(a.game_name).toLowerCase();
        const bName = getGameDisplayName(b.game_name).toLowerCase();
        comparison = aName > bName ? 1 : aName < bName ? -1 : 0;
      }

      return comparison;
    });
  };

  const getGameDisplayName = (gameName) => {
    const game = allGames.find(g => g.name === gameName);
    return game ? game.displayName : gameName;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString();
  };


  // Separate played and unplayed games
  const playedGames = gameStats.filter(stat => stat.total_games_played > 0);
  const playedGameNames = gameStats.map(stat => stat.game_name);
  const unplayedGames = allGames.filter(game => !playedGameNames.includes(game.name));

  if (loading || isLoading) {
    return (
      <div className="stats-page">
        <div className="stats-page__container">
          <div className="stats-page__loading">Loading...</div>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return (
    <div className="stats-page">
      <div className="stats-page__container">
        <div className="stats-page__header">
          <h1>Game Statistics</h1>
          <p>Your detailed gaming statistics and achievements</p>
        </div>

        {summary && (
          <div className="stats-page__summary">
            <div className="stats-page__summary-grid">
              <div className="stats-page__summary-card">
                <div className="stats-page__summary-number">{summary.total_games}</div>
                <div className="stats-page__summary-label">Total Games</div>
              </div>
              <div className="stats-page__summary-card">
                <div className="stats-page__summary-number">{summary.total_daily_games}</div>
                <div className="stats-page__summary-label">Daily Games</div>
              </div>
              <div className="stats-page__summary-card">
                <div className="stats-page__summary-number">{summary.overall_best_streak}</div>
                <div className="stats-page__summary-label">Best Streak</div>
              </div>
            </div>
          </div>
        )}

        <div className="stats-page__section">
          <h2>Games You've Played</h2>
          {playedGames.length > 0 ? (
            <div className="stats-page__table-container">
              <table className="stats-page__table">
                <thead>
                  <tr>
                    <th onClick={() => handleSort('username')} className="stats-page__header--sortable">
                      <span className="stats-page__header-content">
                        Name
                        <span className={`stats-page__sort-icon ${sortField === 'username' ? 'stats-page__sort-icon--visible' : ''}`}>
                          {sortField === 'username' && (sortDirection === 'asc' ? '↑' : '↓')}
                        </span>
                      </span>
                    </th>
                    <th onClick={() => handleSort('current_daily_streak')} className="stats-page__header--sortable">
                      <span className="stats-page__header-content">
                        Current Streak
                        <span className={`stats-page__sort-icon ${sortField === 'current_daily_streak' ? 'stats-page__sort-icon--visible' : ''}`}>
                          {sortField === 'current_daily_streak' && (sortDirection === 'asc' ? '↑' : '↓')}
                        </span>
                      </span>
                    </th>
                    <th onClick={() => handleSort('best_daily_streak')} className="stats-page__header--sortable">
                      <span className="stats-page__header-content">
                        Best Streak
                        <span className={`stats-page__sort-icon ${sortField === 'best_daily_streak' ? 'stats-page__sort-icon--visible' : ''}`}>
                          {sortField === 'best_daily_streak' && (sortDirection === 'asc' ? '↑' : '↓')}
                        </span>
                      </span>
                    </th>
                    <th onClick={() => handleSort('total_daily_games_played')} className="stats-page__header--sortable">
                      <span className="stats-page__header-content">
                        Daily Games
                        <span className={`stats-page__sort-icon ${sortField === 'total_daily_games_played' ? 'stats-page__sort-icon--visible' : ''}`}>
                          {sortField === 'total_daily_games_played' && (sortDirection === 'asc' ? '↑' : '↓')}
                        </span>
                      </span>
                    </th>
                    <th onClick={() => handleSort('total_games_played')} className="stats-page__header--sortable">
                      <span className="stats-page__header-content">
                        Total Games
                        <span className={`stats-page__sort-icon ${sortField === 'total_games_played' ? 'stats-page__sort-icon--visible' : ''}`}>
                          {sortField === 'total_games_played' && (sortDirection === 'asc' ? '↑' : '↓')}
                        </span>
                      </span>
                    </th>
                    <th onClick={() => handleSort('last_played_date')} className="stats-page__header--sortable">
                      <span className="stats-page__header-content">
                        Last Played
                        <span className={`stats-page__sort-icon ${sortField === 'last_played_date' ? 'stats-page__sort-icon--visible' : ''}`}>
                          {sortField === 'last_played_date' && (sortDirection === 'asc' ? '↑' : '↓')}
                        </span>
                      </span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortData(playedGames).map((stat) => (
                    <tr key={stat.game_name}>
                      <td>{getGameDisplayName(stat.game_name)}</td>
                      <td style={{textAlign: 'center'}}>{currentStreaks[stat.game_name] || 0}</td>
                      <td style={{textAlign: 'center'}}>{stat.best_daily_streak}</td>
                      <td style={{textAlign: 'center'}}>{stat.total_daily_games_played}</td>
                      <td style={{textAlign: 'center'}}>{stat.total_games_played}</td>
                      <td>{formatDate(stat.last_played_date)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="stats-page__empty-state">
              <p>🎮 No games played yet!</p>
              <p>Start playing to see your statistics here.</p>
            </div>
          )}
        </div>

        <div className="stats-page__section">
          <h2>Games You Haven't Tried</h2>
          {unplayedGames.length > 0 ? (
            <div className="stats-page__table-container">
              <table className="stats-page__table">
                <thead>
                  <tr>
                    <th>Name</th>
                  </tr>
                </thead>
                <tbody>
                  {unplayedGames.map((game) => (
                    <tr key={game.name}>
                      <td>{game.displayName}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="stats-page__empty-state">
              <p>You've already tried all the currently released games!</p>
              <p>Check back later for new games to play.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Stats;